/**
 * PDF Color Sanitizer Utility
 * 
 * Converts modern CSS color spaces (such as OKLCH generated by Tailwind CSS v4)
 * into standard sRGB hexadecimal or rgb/rgba values before HTML2Canvas captures
 * the DOM tree.
 * 
 * This prevents html2canvas from failing or misinterpreting modern color definitions
 * as dark/black fallback blocks.
 */

/**
 * Converts OKLCH color string into sRGB hex or rgba string
 */
export function oklchToRgb(colorStr: string): string {
  // Matches: oklch(L C H [/ A])
  // Supports: percentages, degrees, radians, turns, and 'none' keywords
  const regex = /oklch\(\s*([\d.]+%?|none)\s*(?:,\s*|\s+)([\d.]+%?|none)\s*(?:,\s*|\s+)([\d.]+(?:deg|rad|turn)?|none)(?:\s*(?:\/|,)\s*([\d.]+%?|none))?\s*\)/i;
  const match = colorStr.match(regex);
  if (!match) return colorStr;

  const [, lStr, cStr, hStr, aStr] = match;

  try {
    let L = 0;
    if (lStr && lStr.toLowerCase() !== 'none') {
      L = lStr.endsWith('%') ? parseFloat(lStr) / 100 : parseFloat(lStr);
    }

    let C = 0;
    if (cStr && cStr.toLowerCase() !== 'none') {
      C = cStr.endsWith('%') ? parseFloat(cStr) / 100 : parseFloat(cStr);
    }

    let H = 0;
    if (hStr && hStr.toLowerCase() !== 'none') {
      H = parseFloat(hStr);
      if (hStr.endsWith('rad')) H = (H * 180) / Math.PI;
      else if (hStr.endsWith('turn')) H = H * 360;
    }

    let alpha = 1;
    if (aStr && aStr.toLowerCase() !== 'none') {
      alpha = aStr.endsWith('%') ? parseFloat(aStr) / 100 : parseFloat(aStr);
    }

    // Convert OKLCH -> OKLab
    const hRad = (H * Math.PI) / 180;
    const a = C * Math.cos(hRad);
    const b = C * Math.sin(hRad);

    // Convert OKLab -> linear LMS
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;

    // Convert linear LMS -> linear sRGB
    const rLin = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    const gLin = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    const bLin = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

    // Gamma correction to non-linear sRGB
    const gamma = (c: number) => {
      const clamped = Math.max(0, Math.min(1, c));
      return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
    };

    const r = Math.round(gamma(rLin) * 255);
    const g = Math.round(gamma(gLin) * 255);
    const bVal = Math.round(gamma(bLin) * 255);

    if (alpha < 1) {
      return `rgba(${r}, ${g}, ${bVal}, ${Math.round(alpha * 100) / 100})`;
    }

    return '#' + [r, g, bVal].map((x) => x.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.warn('Could not parse oklch color:', colorStr, err);
    return colorStr;
  }
}

/**
 * Replaces all OKLCH occurrences in a CSS string with their sRGB counterparts
 */
export function replaceOklchWithRgb(cssText: string): string {
  if (!cssText || !cssText.includes('oklch')) return cssText;

  const oklchGlobalRegex = /oklch\(\s*([\d.]+%?|none)\s*(?:,\s*|\s+)([\d.]+%?|none)\s*(?:,\s*|\s+)([\d.]+(?:deg|rad|turn)?|none)(?:\s*(?:\/|,)\s*([\d.]+%?|none))?\s*\)/gi;

  return cssText.replace(oklchGlobalRegex, (match) => {
    return oklchToRgb(match);
  });
}

/**
 * Sanitizes all style tags, inline styles, and computed properties inside a cloned document
 * so that html2canvas will render accurate colors without blacked-out boxes.
 */
export function sanitizeClonedDocForPdf(clonedDoc: Document): void {
  // 1. Sanitize all <style> tags (especially Tailwind CSS v4 variables & rules)
  const styleElements = clonedDoc.querySelectorAll('style');
  styleElements.forEach((styleTag) => {
    if (styleTag.textContent && styleTag.textContent.includes('oklch')) {
      styleTag.textContent = replaceOklchWithRgb(styleTag.textContent);
    }
  });

  // 2. Sanitize any inline style attributes
  const allElements = clonedDoc.querySelectorAll('*');
  allElements.forEach((el) => {
    const styleAttr = el.getAttribute('style');
    if (styleAttr && styleAttr.includes('oklch')) {
      el.setAttribute('style', replaceOklchWithRgb(styleAttr));
    }
  });

  // 3. Fallback: inspect computed styles in the cloned document view
  // If an element still computes to an oklch color (e.g. from linked stylesheets or CSS variables),
  // apply the resolved sRGB value directly as an inline style.
  try {
    const win = clonedDoc.defaultView || (typeof window !== 'undefined' ? window : null);
    if (win) {
      allElements.forEach((el) => {
        if (el instanceof win.HTMLElement || el instanceof HTMLElement) {
          const comp = win.getComputedStyle(el);
          const colorProps: (keyof CSSStyleDeclaration)[] = [
            'backgroundColor',
            'color',
            'borderTopColor',
            'borderRightColor',
            'borderBottomColor',
            'borderLeftColor',
            'outlineColor',
          ];

          for (const prop of colorProps) {
            const val = comp[prop];
            if (typeof val === 'string' && val.includes('oklch')) {
              (el.style as any)[prop] = replaceOklchWithRgb(val);
            }
          }

          if (typeof comp.boxShadow === 'string' && comp.boxShadow.includes('oklch')) {
            el.style.boxShadow = replaceOklchWithRgb(comp.boxShadow);
          }
        }
      });
    }
  } catch (e) {
    // Non-fatal, just a progressive enhancement
    console.warn('Computed style scan in sanitizeClonedDocForPdf skipped:', e);
  }
}
